/**
 * GWP (Gift With Purchase) Manager - Optimized for Shopify OS 2.0
 *
 * This script automatically adds or removes gift products from the Shopify cart
 * based on a unified, configurable campaign object. It combines immediate event handling
 * with a polling fallback for maximum reliability.
 *
 * @version 4.2.0
 * @author Viney Bishnoi
 */
class GWPManager {
  /**
   * Initializes the GWP Manager.
   */
  constructor() {
    this.isProcessing = false;
    this.checkTimer = null; // Timer for delayed GWP checks
    this.postSkioTimer = null; // Timer for post-Skio updates
    this.isUpdatingUI = false; // Flag to prevent reacting to own UI updates
    this.config = window.gwp?.config; // Cache config since it's server-side rendered
    this.miniCart = null; // Will be set in init()

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  /**
   * Core initialization logic. Sets up event listeners and starts the cart monitor.
   */
  init() {
    // Early validation - if no config is available, log error and exit
    if (!this.config) {
      console.error('🚫 GWP Manager: No configuration found. Ensure window.gwp.config is available.');
      return;
    }

    // Cache mini-cart reference for better performance
    this.miniCart = document.querySelector('mini-cart');

    // Listen for standard and custom cart update events
    const cartUpdateEvents = ['cart:updated', 'cartUpdated', 'ajaxProduct:added'];
    cartUpdateEvents.forEach((event) => {
      document.addEventListener(event, this.handleCartUpdate.bind(this));
    });

    // Subscribe to Shopify's modern pub/sub events
    if (window.subscribe?.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
    }

    // Perform initial check
    this.checkGWPConditions();
  }

  /**
   * Handles incoming cart update events with proper sequencing.
   * @param {Event} event - The cart update event object.
   */
  handleCartUpdate(event) {
    // Ignore updates triggered by this script to prevent infinite loops
    if (this.isProcessing || this.isUpdatingUI || event?.detail?.source === 'gwp-manager') {
      return;
    }

    // Clear any existing timer to debounce rapid events
    clearTimeout(this.checkTimer);

    if (event.type === 'cartUpdated') {
      // For Skio events, don't schedule immediate check - let Skio notify us when ready
      return; // Skio will call schedulePostSkioUpdate() when ready
    }

    // Set timer to check GWP conditions after other operations settle
    this.checkTimer = setTimeout(() => {
      this.checkGWPConditions();
    }, 150);
  }

  /**
   * Called by Skio after it completes its section updates
   * This ensures GWP checks happen after Skio's priority updates
   */
  schedulePostSkioUpdate() {
    clearTimeout(this.postSkioTimer);

    this.postSkioTimer = setTimeout(() => {
      this.checkGWPConditions();
    }, 200); // Short delay to ensure Skio's UI updates are complete
  }

  /**
   * Checks if the campaign is currently active based on config flags.
   * @returns {boolean}
   */
  isCampaignCurrentlyActive() {
    // Campaign active status is determined by the 'enable' flag
    return !!(this.config && this.config.enable);
  }

  /**
   * The main logic loop. Fetches the cart and routes to the correct campaign logic.
   */
  async checkGWPConditions() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      const cart = await this.getCurrentCart();

      if (!this.isCampaignCurrentlyActive()) {
        await this.removeAllGifts(cart); // Clean up if campaign is disabled
        return;
      }

      if (this.config.campaignType === 'threshold') {
        await this.handleThresholdCampaign(cart);
      } else if (this.config.campaignType === 'product_specific') {
        await this.handleProductSpecificCampaign(cart);
      }
    } catch (error) {
      console.error('GWP Error:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Gets sections to render for cart updates, using cached mini-cart reference
   * @returns {Array} Array of section IDs
   */
  getSectionsToRender() {
    return this.miniCart?.getSectionsToRender ? this.miniCart.getSectionsToRender().map((s) => s.id) : [];
  }

  /**
   * Updates cart UI using cached mini-cart reference without triggering events
   * This is the final authoritative update after Skio completes its changes
   * @param {Object} result - Cart API response
   */
  updateCartUI(result) {
    // Set flag to ignore events triggered by UI updates
    this.isUpdatingUI = true;

    try {
      // Update cart UI using mini-cart's render method (final update)
      if (this.miniCart?.renderContents) {
        this.miniCart.renderContents(result);
      }

      // Verify gift visibility after final update
      setTimeout(() => {
        this.ensureGiftIsVisible();
      }, 150);
    } finally {
      // Clear flag after update completes
      setTimeout(() => {
        this.isUpdatingUI = false;
      }, 300);
    }
  }

  /**
   * Ensures the gift product is visible in the UI after updates
   */
  ensureGiftIsVisible() {
    if (!this.config?.enable || !this.config?.giftProduct?.length) return;

    // Check if gift should be visible based on current cart
    this.getCurrentCart()
      .then((cart) => {
        const hasGift = this.hasGiftProduct(cart, this.config.giftProduct, this.config.campaignType);

        if (hasGift) {
          // Gift exists in cart data, ensure it's visible in UI
          const giftElements = document.querySelectorAll('[data-variant-id]');
          const giftVariantId = this.config.giftProduct[0]?.variantId;

          let giftVisible = false;
          giftElements.forEach((element) => {
            if (element.dataset.variantId == giftVariantId) {
              giftVisible = true;
            }
          });

          if (!giftVisible) {
            this.forceCartRefresh();
          }
        }
      })
      .catch(console.error);
  }

  /**
   * Forces a complete cart refresh to sync UI with cart data
   */
  forceCartRefresh() {
    const miniCart = document.querySelector('mini-cart');
    if (miniCart) {
      fetch('/cart.js')
        .then((response) => response.json())
        .then((freshCart) => {
          const mockResponse = {
            ...freshCart,
            sections: {},
          };

          if (miniCart.renderContents) {
            miniCart.renderContents(mockResponse);
          }
        })
        .catch(console.error);
    }
  }

  /**
   * Fetches the complete current cart state from Shopify's Cart API.
   * @returns {Promise<object>} A promise that resolves with the cart object.
   */
  async getCurrentCart() {
    // Add cache buster to ensure fresh cart data
    const cacheBuster = Date.now();
    const response = await fetch(`/cart.js?t=${cacheBuster}`);
    if (!response.ok) throw new Error('Failed to fetch cart data');
    const cart = await response.json();

    return cart;
  }

  /**
   * Handles the logic for "spend $X, get a gift" campaigns.
   * @param {object} cart - The current cart object.
   */
  async handleThresholdCampaign(cart) {
    if (!this.config?.threshold || !this.config.giftProduct?.length) return;

    // Calculate cart total excluding free items, with detailed logging
    let cartTotal = 0;
    const itemBreakdown = [];

    cart.items.forEach((item) => {
      const isFree = item.properties?._isFree === 'true';
      const itemTotal = item.final_line_price;

      if (!isFree) {
        cartTotal += itemTotal;
        itemBreakdown.push(`${item.product_title}: $${(itemTotal / 100).toFixed(2)}`);
      } else {
        itemBreakdown.push(`${item.product_title}: FREE (excluded)`);
      }
    });

    const threshold = this.config.threshold * 100;
    const meetsThreshold = cartTotal >= threshold;
    const hasGift = this.hasGiftProduct(cart, this.config.giftProduct, 'threshold');

    // Threshold calculation for debugging (if needed)
    // console.log(`🧮 Threshold calculation breakdown:`, itemBreakdown);
    // console.log(`💰 Total: $${(cartTotal / 100).toFixed(2)} vs Threshold: $${this.config.threshold.toFixed(2)} | Meets: ${meetsThreshold} | Has gift: ${hasGift}`);

    if (meetsThreshold && !hasGift) {
      await this.addGiftProduct(this.config.giftProduct[0], 'threshold');
    } else if (!meetsThreshold && hasGift) {
      await this.removeGiftProducts(cart, this.config.giftProduct, 'threshold');
    }
  }

  /**
   * Handles logic for "buy product X, get gift Y" campaigns.
   * @param {object} cart - The current cart object.
   */
  async handleProductSpecificCampaign(cart) {
    if (!this.config.triggerProducts?.length || !this.config.giftProduct?.length) return;

    const hasTrigger = cart.items.some((item) =>
      this.config.triggerProducts.some((trigger) => trigger.id === item.product_id)
    );
    const hasGift = this.hasGiftProduct(cart, this.config.giftProduct, 'product_specific');

    // Product-specific campaign debugging (if needed)
    // console.log(`🎯 Product-Specific: Trigger: ${hasTrigger} | Has gift: ${hasGift}`);

    if (hasTrigger && !hasGift) {
      await this.addGiftProduct(this.config.giftProduct[0], 'product_specific');
    } else if (!hasTrigger && hasGift) {
      await this.removeGiftProducts(cart, this.config.giftProduct, 'product_specific');
    }
  }

  /**
   * Checks if a specific gift product for a campaign is already in the cart.
   * @param {object} cart - The current cart object.
   * @param {Array<object>} giftProducts - The gift products for the campaign.
   * @param {string} campaignType - The identifier for the campaign.
   * @returns {boolean} True if the gift is in the cart.
   */
  hasGiftProduct(cart, giftProducts, campaignType) {
    return cart.items.some(
      (item) =>
        item.properties?._isFree === 'true' &&
        item.properties?._giftCampaign === campaignType &&
        giftProducts.some((gift) => gift.id === item.product_id)
    );
  }

  /**
   * Adds a specified gift product to the cart.
   * @param {object} giftProduct - The gift product object to add.
   * @param {string} campaignType - The identifier for the campaign.
   */

  async addGiftProduct(giftProduct, campaignType) {
    if (!giftProduct?.variantId) {
      console.error('Invalid gift product configuration:', giftProduct);
      return;
    }
    try {
      // console.log(`🎁 Adding gift: ${giftProduct.title} (${campaignType})`);

      // Define base properties
      const properties = {
        _isFree: 'true',
        _giftCampaign: campaignType,
      };

      // Conditionally add the threshold property
      if (campaignType === 'threshold') {
        properties._hasTh = this.config?.threshold;
      }

      const sections = this.getSectionsToRender();
      const cartAddUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';

      const response = await fetch(cartAddUrl, {
        ...(window.fetchConfig?.('json') || { method: 'POST', headers: { 'Content-Type': 'application/json' } }),
        body: JSON.stringify({
          id: giftProduct.variantId,
          quantity: 1,
          properties: properties,
          sections: sections,
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Failed to add gift');

      this.updateCartUI(result);

      this.showNotification(`🎁 Free gift added: ${giftProduct.title}`, 'success');
    } catch (error) {
      console.error('Error adding gift product:', error);
      this.showNotification(error.message, 'error');
    }
  }

  /**
   * Identifies and removes gift products for a specific campaign.
   */
  async removeGiftProducts(cart, giftProducts, campaignType) {
    const giftItems = cart.items.filter(
      (item) =>
        item.properties?._isFree === 'true' &&
        item.properties?._giftCampaign === campaignType &&
        giftProducts.some((gift) => gift.id === item.product_id)
    );
    if (!giftItems.length) return;
    await this.updateCartWithRemovals(giftItems, `Gift removed: ${giftItems[0].product_title}`);
  }

  /**
   * Identifies and removes ALL gift products, regardless of campaign.
   */
  async removeAllGifts(cart) {
    const allGiftItems = cart.items.filter((item) => item.properties?._isFree === 'true');
    if (!allGiftItems.length) return;
    await this.updateCartWithRemovals(allGiftItems, 'Your free gift was removed.');
  }

  /**
   * OPTIMIZED: Centralized logic to remove multiple items from the cart in a single API call.
   * @param {Array<object>} itemsToRemove - An array of cart item objects to remove.
   * @param {string} notificationMessage - The message to show the user upon removal.
   */
  async updateCartWithRemovals(itemsToRemove, notificationMessage) {
    try {
      const sections = this.getSectionsToRender();
      const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';

      const updates = {};
      itemsToRemove.forEach((item) => {
        updates[item.key] = 0;
      });

      const response = await fetch(cartUpdateUrl, {
        ...(window.fetchConfig?.('json') || { method: 'POST', headers: { 'Content-Type': 'application/json' } }),
        body: JSON.stringify({
          updates,
          sections: sections,
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Failed to remove gift(s)');

      this.updateCartUI(result);

      this.showNotification(notificationMessage, 'info');
    } catch (error) {
      console.error('Error removing gift products:', error);
      this.showNotification(error.message, 'error');
    }
  }

  /**
   * Displays a simple, temporary notification to the user.
   */
  showNotification(message, type = 'info') {
    const colors = { success: '#28a745', error: '#dc3545', info: '#17a2b8' };
    const notification = document.createElement('div');
    notification.className = 'gwp-notification';
    notification.style.cssText = `
      position: fixed; top: 20px; right: 20px; z-index: 10001;
      background: ${colors[type]}; color: white; padding: 12px 20px;
      border-radius: 5px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      font: 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      max-width: 320px; opacity: 0; transform: translateX(110%);
      transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    `;
    notification.textContent = message;
    document.body.appendChild(notification);

    // Animate in
    requestAnimationFrame(() => {
      notification.style.opacity = '1';
      notification.style.transform = 'translateX(0)';
    });

    // Auto-remove after 4 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(110%)';
      setTimeout(() => notification.remove(), 400);
    }, 2000);
  }

  /**
   * Cleanup method to clear timers
   */
  destroy() {
    if (this.checkTimer) {
      clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }
    if (this.postSkioTimer) {
      clearTimeout(this.postSkioTimer);
      this.postSkioTimer = null;
    }
  }
}

// Instantiate the manager.
window.gwpManager = new GWPManager();
